import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed } from "vue";
import http from "@/helper/http";
import { RESOURCE_CACHE, queryKeys } from "@/config/queryConfig";

// ==================== TRANSFERS ====================

export function useTransfers(groupId, filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.transfers(groupId, filters),
    queryFn: async () => {
      const params = {
        sort: "-created_at",
        ...filters,
      };
      const response = await http.get(`/v2/groups/${groupId}/transfers`, {
        params,
      });
      return response.data;
    },
    enabled: computed(() => !!groupId),
    ...RESOURCE_CACHE.transfers,
    ...options,
  });
}

export function useTransferDetails(groupId, transferId, options = {}) {
  return useQuery({
    queryKey: queryKeys.transfer(groupId, transferId),
    queryFn: async () => {
      const response = await http.get(
        `/v2/groups/${groupId}/transfers/${transferId}`
      );
      return response.data;
    },
    enabled: computed(() => !!groupId && !!transferId),
    ...RESOURCE_CACHE.transfers,
    ...options,
  });
}

// ==================== TRANSFER REQUESTS ====================

export function useTransferRequests(groupId, filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.transferRequests(groupId, filters),
    queryFn: async () => {
      const params = {
        sort: "-created_at",
        ...filters,
      };
      const response = await http.get(
        `/v2/groups/${groupId}/transfer-requests`,
        { params }
      );
      return response.data;
    },
    enabled: computed(() => !!groupId),
    ...RESOURCE_CACHE.transferRequests,
    ...options,
  });
}

export function useTransferRequestDetails(groupId, requestId, options = {}) {
  return useQuery({
    queryKey: queryKeys.transferRequest(groupId, requestId),
    queryFn: async () => {
      const response = await http.get(
        `/v2/groups/${groupId}/transfer-requests/${requestId}`
      );
      return response.data;
    },
    enabled: computed(() => !!groupId && !!requestId),
    ...RESOURCE_CACHE.transferRequests,
    ...options,
  });
}

export function useCreateTransferRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, payload }) => {
      const response = await http.post(
        `/v2/groups/${groupId}/transfer-requests`,
        payload
      );
      return response.data;
    },
    onSuccess: (data, { groupId }) => {
      // Invalidate and refetch transfer requests
      queryClient.invalidateQueries({
        queryKey: ["transferRequests", groupId],
      });
    },
  });
}

export function useUpdateTransferRequestStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ groupId, requestId, status, note }) => {
      const response = await http.patch(
        `/v2/groups/${groupId}/transfer-requests/${requestId}/status`,
        { status, note }
      );
      return response.data;
    },
    onSuccess: (data, { groupId, requestId }) => {
      // Invalidate both requests and transfers (if completed)
      queryClient.invalidateQueries({
        queryKey: ["transferRequests", groupId],
      });
      queryClient.invalidateQueries({
        queryKey: ["transferRequest", groupId, requestId],
      });
      if (data?.transfer_id) {
        queryClient.invalidateQueries({ queryKey: ["transfers", groupId] });
      }
    },
  });
}

// ==================== INVENTORY REMOVALS ====================

export function useRemovals(storeId, page = 1, options = {}) {
  return useQuery({
    queryKey: queryKeys.removals(storeId, page),
    queryFn: async () => {
      const params = {
        page,
        per_page: options.perPage || 20,
        sort: "-created_at",
      };
      const response = await http.get(
        `/v2/store/inventory/${storeId}/removals`,
        { params }
      );
      return response.data;
    },
    enabled: computed(() => !!storeId),
    ...RESOURCE_CACHE.removals,
    ...options,
  });
}

// ==================== HELPER FOR REFRESH ====================

export function useInvalidateTransfers() {
  const queryClient = useQueryClient();

  return {
    invalidateTransfers: (groupId) =>
      queryClient.invalidateQueries({ queryKey: ["transfers", groupId] }),
    invalidateRequests: (groupId) =>
      queryClient.invalidateQueries({
        queryKey: ["transferRequests", groupId],
      }),
    invalidateRemovals: (storeId) =>
      queryClient.invalidateQueries({ queryKey: ["removals", storeId] }),
    invalidateAll: () => queryClient.invalidateQueries(),
  };
}
